import { PROTOCOL_TEST_IDS } from '@/components/admin/protocoles/contracts';
import type { LevelObjective } from '@/lib/analyst-reading';
import {
  ANALYST_LEVEL_CHOICES,
  levelCountsLine,
  levelGroups,
  levelsPastille,
} from '@/lib/analyst-reading';
import { cn } from '@/lib/utils';
import type { AnalystLevel, ProtocolStepDraft } from '@matter_extractor/shared';
import { analystLevelCounts, carriedAnalystLevels } from '@matter_extractor/shared';
import { Icon } from '@pms-digital/design-system';
import { useId } from 'react';

/**
 * Le groupe « Niveaux des objectifs » de la section Analyste (analyste.md §3.1,
 * §3.5, K1 ; planche `AnalysteA`) : les objectifs du protocole GROUPÉS PAR ÉTAPE,
 * chacun avec un groupe radio segmenté « Aucun | Pourquoi | Comment | Quoi », et
 * sous la liste la ligne des comptes avec sa pastille.
 *
 * ## The level is set here and nowhere else (K1)
 *
 * It is a field of the OBJECTIVE (`analystLevel`), written by the one function
 * `withAnalystLevel` into the draft's steps - so it follows the objective's renames,
 * its copy and its deletion, and the step editor knows nothing of it. A level can
 * carry as many objectives as the protocol wants, and an objective with no level is
 * not read by the first pass (§3.1): « Aucun » is the first choice and the default.
 *
 * ## REAL radios, one native group per objective
 *
 * The same recipe as the switch of the editor (`protocol-view-switch.tsx`): a native
 * radio that FILLS its segment, transparent, so the browser brings the arrow keys,
 * the grouping and the announced state, and a Playwright `.check()` reaches the
 * control an operator clicks. The group is named by the objective's name, and each
 * radio by its label - « Comment » -, which is what `getByRole('radio', { name })`
 * resolves inside a row. The name of each group comes from `useId()`: a literal
 * would make every objective of the page ONE group, and a click on a row would
 * clear another.
 *
 * ## It wraps on a phone, and nothing overflows
 *
 * A row is the objective's name and its four segments (about 230 px): side by side
 * from the width where both fit, and the segments under the name where they do not,
 * which is what `flex-wrap` does by itself - at 375 px the name takes its line, the
 * segments theirs, and `admin-responsive.spec.ts` keeps the body from scrolling
 * sideways. A very long name breaks inside its own line.
 *
 * ## The counts, and the pastille that says what is missing
 *
 * « Pourquoi : 2 objectifs · Comment : 2 · Quoi : 2 », and beside it a green
 * pastille « chaque niveau a un objectif » or a yellow one that names what lacks
 * (« Comment n'a aucun objectif »). The counts are those of `shared`
 * (`analystLevelCounts`), the function the editor's mark, the boxes and
 * `check_protocol` read the same section with.
 */

export interface AnalystLevelsProps {
  /** The steps of the draft - or of the version « Voir » shows. */
  readonly steps: readonly ProtocolStepDraft[];
  /** « Voir », or a retired protocol: nothing is settable. */
  readonly readOnly: boolean;
  /** The id of the yellow encart when it is drawn: the group is described by it, as the maquette has it. */
  readonly encartId: string | null;
  readonly onLevel: (objectiveId: string, level: AnalystLevel | null) => void;
}

export function AnalystLevels({ steps, readOnly, encartId, onLevel }: AnalystLevelsProps) {
  const helpId = useId();
  const groups = levelGroups(steps);
  const counts = analystLevelCounts(carriedAnalystLevels(steps));
  const pastille = levelsPastille(counts);

  return (
    <fieldset
      data-testid={PROTOCOL_TEST_IDS.analystLevels}
      aria-describedby={encartId === null ? helpId : `${encartId} ${helpId}`}
      className="flex min-w-0 flex-col gap-2 border-0 p-0"
    >
      <legend className="font-semibold text-gray-800 text-small">Niveaux des objectifs</legend>
      <p id={helpId} className="mt-0.5 text-caption text-gray-600 leading-4">
        La passe 1 lit les objectifs qui portent un niveau, sans savoir lequel. Le code s’en sert
        pour fixer les niveaux certains et éliminer les noyaux impossibles.
      </p>
      <div className="flex flex-col gap-3 rounded-md border-2 border-gray-200 px-3.5 py-3">
        {groups.length === 0 ? (
          <p className="text-gray-600 text-small">
            Aucun objectif dans ce protocole pour l’instant.
          </p>
        ) : (
          groups.map((group) => (
            <div key={group.stepId} className="flex flex-col gap-0.5">
              <p className="font-semibold text-caption text-gray-600 uppercase tracking-wide">
                {group.title}
              </p>
              {group.objectives.map((objective) => (
                <LevelRow
                  key={objective.id}
                  objective={objective}
                  readOnly={readOnly}
                  onLevel={onLevel}
                />
              ))}
            </div>
          ))
        )}
      </div>
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5">
        <p data-testid={PROTOCOL_TEST_IDS.analystCounts} className="text-caption text-gray-700">
          {levelCountsLine(counts)}
        </p>
        <span
          data-testid={PROTOCOL_TEST_IDS.analystPastille}
          data-state={pastille.complete ? 'complete' : 'incomplete'}
          className={cn(
            'inline-flex items-center gap-1 whitespace-nowrap rounded-full px-2.5 py-0.5 font-semibold text-caption',
            pastille.complete ? 'bg-vert-tint text-vert-deep' : 'bg-jaune-tint text-jaune-deep',
          )}
        >
          {pastille.complete ? (
            <Icon name="check_circle" size={14} />
          ) : (
            <Icon name="warning" size={14} />
          )}
          <span>{pastille.sentence}</span>
        </span>
      </div>
    </fieldset>
  );
}

interface LevelRowProps {
  readonly objective: LevelObjective;
  readonly readOnly: boolean;
  readonly onLevel: (objectiveId: string, level: AnalystLevel | null) => void;
}

/** One objective: its name, and the four segments that say its level. */
function LevelRow({ objective, readOnly, onLevel }: LevelRowProps) {
  const nameId = useId();
  const group = useId();
  const named = objective.name.trim() !== '';

  return (
    <div
      data-testid={PROTOCOL_TEST_IDS.analystLevelRow}
      data-objective-id={objective.id}
      data-level={objective.level ?? 'none'}
      className="flex min-h-8 flex-wrap items-center justify-between gap-x-4 gap-y-1"
    >
      <span
        id={nameId}
        className={cn(
          'min-w-0 break-words text-small',
          named ? 'text-gray-800' : 'text-gray-500 italic',
        )}
      >
        {named ? objective.name : 'Objectif sans nom'}
      </span>
      <div
        role="radiogroup"
        aria-labelledby={nameId}
        className="has-[:focus-visible]:-outline-offset-2 inline-flex shrink-0 overflow-hidden rounded-md border-2 border-gray-300 bg-white has-[:focus-visible]:outline-2 has-[:focus-visible]:outline-bleu-nuit"
      >
        {ANALYST_LEVEL_CHOICES.map((choice, index) => {
          const chosen = choice.level === objective.level;
          return (
            <label
              key={choice.value}
              className={cn(
                'relative cursor-pointer select-none whitespace-nowrap px-2.5 py-[3px] font-semibold text-caption transition-colors has-[:disabled]:cursor-default',
                index === 0 ? '' : 'border-gray-300 border-l',
                chosen ? 'bg-bleu-nuit text-white' : 'text-gray-700 hover:bg-bleu-nuit-tint',
              )}
            >
              <input
                type="radio"
                name={group}
                value={choice.value}
                checked={chosen}
                disabled={readOnly}
                data-testid={PROTOCOL_TEST_IDS.analystLevelChoice}
                data-level={choice.value}
                onChange={() => onLevel(objective.id, choice.level)}
                className="absolute inset-0 m-0 h-full w-full cursor-pointer appearance-none opacity-0 focus:outline-none disabled:cursor-default"
              />
              {choice.label}
            </label>
          );
        })}
      </div>
    </div>
  );
}
